import type { QueryClient } from '@tanstack/react-query';

/** Salons whose caches stay in memory after leaving them, so going back paints the last data at once. */
export const SCOPED_QUERY_CLIENT_CACHE_SIZE = 3;

interface ScopedQueryClientEntry {
  /** Exact access scope: user, salon, actor type, access revision, agency role. Reuse needs an identical string. */
  scope: string;
  /** User + salon the scope belongs to: a newer scope for them (revision / role change) replaces the old cache. */
  owner: string;
  userId: string;
  client: QueryClient;
  /** Left (not on screen): no request may start in it, see {@link blockFetchesWhileReleased}. */
  released: boolean;
  unsubscribe: () => void;
}

/** Most recently used last. Memory only: nothing here outlives the tab. */
let entries: ScopedQueryClientEntry[] = [];

/**
 * Requests carry the ACTIVE salon's header, so a request starting in a salon's cache after leaving it (a mutation's
 * late `onSettled` refetch, a timer) would store the next salon's answer under this salon's keys. While the cache is
 * released, any fetch that starts is cancelled at once and the query keeps its last data.
 */
function blockFetchesWhileReleased(entry: ScopedQueryClientEntry) {
  return entry.client.getQueryCache().subscribe((event) => {
    if (!entry.released || event.type !== 'updated' || event.action.type !== 'fetch') return;
    // The fetch action is dispatched just before the request starts; cancel once it exists, unless the salon came
    // back on screen meanwhile (a StrictMode effect replay releases and re-marks it in the same commit).
    queueMicrotask(() => {
      if (entry.released) void event.query.cancel({ revert: true });
    });
  });
}

function dispose(entry: ScopedQueryClientEntry) {
  entry.unsubscribe();
  // Cancelling first keeps a non-abortable request from writing into the dropped cache.
  void entry.client.cancelQueries();
  entry.client.clear();
}

/**
 * The per-salon QueryClient for `scope`: the cached one when this exact scope was used recently (a salon hop
 * A → B → A) and `reuse` allows it, otherwise a new one from `create`. A reused cache paints its last data at once
 * and every page refetches as it mounts (changes made meanwhile, e.g. agency feature switches, show up). A different
 * user drops every cached client; a new scope for the same user + salon (access revision or role changed) drops that
 * salon's old client, so cached data never crosses a permission change. Only the
 * {@link SCOPED_QUERY_CLIENT_CACHE_SIZE} most recent stay; older ones are cleared.
 */
export function acquireScopedQueryClient(
  key: { scope: string; owner: string; userId: string; reuse?: boolean },
  create: () => QueryClient
): QueryClient {
  const { reuse = true, ...identity } = key;
  const existing = entries.find((entry) => entry.scope === identity.scope);
  if (existing && reuse) {
    entries = [...entries.filter((entry) => entry !== existing), existing];
    existing.released = false;
    void existing.client.invalidateQueries({ refetchType: 'none' });
    return existing.client;
  }

  const [kept, dropped] = partition(
    entries,
    (entry) => entry.userId === identity.userId && entry.owner !== identity.owner
  );
  const entry: ScopedQueryClientEntry = { ...identity, client: create(), released: false, unsubscribe: () => undefined };
  entry.unsubscribe = blockFetchesWhileReleased(entry);
  entries = [...kept, entry];
  dropped.push(...entries.splice(0, Math.max(0, entries.length - SCOPED_QUERY_CLIENT_CACHE_SIZE)));
  dropped.forEach(dispose);
  return entry.client;
}

/** The salon's view is (again) on screen: requests may run in its cache. */
export function markScopedQueryClientInUse(client: QueryClient) {
  const entry = entries.find((candidate) => candidate.client === client);
  if (entry) entry.released = false;
}

/**
 * Leaving a salon: requests still in flight are cancelled and put back to their last data, entries that failed
 * (often requests cut off by the switch itself) are dropped so they load fresh next time, and no new request may
 * start in this cache until the salon is opened again. The data stays for a quick return.
 */
export function releaseScopedQueryClient(client: QueryClient) {
  const entry = entries.find((candidate) => candidate.client === client);
  if (entry) entry.released = true;
  void client.cancelQueries();
  client.removeQueries({ predicate: (query) => query.state.status === 'error' });
}

/** Access to a salon could not be verified (revoked, denied): that user's caches of it go at once. */
export function discardScopedQueryClients(owner: string) {
  const [kept, dropped] = partition(entries, (entry) => entry.owner !== owner);
  entries = kept;
  dropped.forEach(dispose);
}

/** Sign-out / account change: every salon cache goes. */
export function clearScopedQueryClients() {
  const dropped = entries;
  entries = [];
  dropped.forEach(dispose);
}

function partition<T>(items: T[], keep: (item: T) => boolean): [T[], T[]] {
  const kept: T[] = [];
  const dropped: T[] = [];
  for (const item of items) (keep(item) ? kept : dropped).push(item);
  return [kept, dropped];
}
